import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Play, X, Camera, Video as VideoIcon, ArrowRight, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { galleryCategories, galleryItems as seedItems } from '../data/gallery';
import { listGallery } from '../lib/galleryApi';

const BASE = process.env.REACT_APP_BACKEND_URL;

const KINDS = [
  { id: 'all',   label: 'All',    icon: ImageIcon },
  { id: 'photo', label: 'Photos', icon: Camera },
  { id: 'video', label: 'Videos', icon: VideoIcon },
];

function spanClass(item) {
  if (item.span === 'wide') return 'md:col-span-2';
  if (item.span === 'tall') return 'md:row-span-2';
  return '';
}

export default function Gallery() {
  const [kind, setKind] = useState('all');
  const [category, setCategory] = useState('all');
  const [activeIdx, setActiveIdx] = useState(null);
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    let off = false;
    listGallery().then((rows) => {
      if (off) return;
      // Prefix backend photo src with BASE so /gallery/<id>.png resolves to full URL.
      const norm = rows.map((r) => r.kind === 'photo' && r.src?.startsWith('/gallery/')
        ? { ...r, src: `${BASE}${r.src}` }
        : r);
      setUploaded(norm);
    }).catch(() => {});
    return () => { off = true; };
  }, []);

  const galleryItems = useMemo(() => [...uploaded, ...seedItems], [uploaded]);

  const filtered = useMemo(() => {
    return galleryItems.filter((it) => {
      if (kind !== 'all' && it.kind !== kind) return false;
      if (category !== 'all' && it.category !== category) return false;
      return true;
    });
  }, [galleryItems, kind, category]);

  const photoCount = galleryItems.filter((i) => i.kind === 'photo').length;
  const videoCount = galleryItems.filter((i) => i.kind === 'video').length;

  const close = useCallback(() => setActiveIdx(null), []);
  const next = useCallback(() => setActiveIdx((i) => (i === null ? null : (i + 1) % filtered.length)), [filtered.length]);
  const prev = useCallback(() => setActiveIdx((i) => (i === null ? null : (i - 1 + filtered.length) % filtered.length)), [filtered.length]);

  useEffect(() => {
    if (activeIdx === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [activeIdx, close, next, prev]);

  const active = activeIdx !== null ? filtered[activeIdx] : null;

  return (
    <div className="bg-white" data-testid="gallery-page">
      <SEO
        title="Photo & Video Gallery — MBBS in Russia | Russian Education"
        description="Browse photos and videos of our partner medical universities in Russia, campus life, alumni journeys, guidance seminars and India-Russia student community moments."
        path="/gallery"
        keywords="MBBS Russia photos, Russian university videos, MBBS gallery, medical campus tour Russia"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-blue-hero">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cpath fill='%23fff' fill-opacity='0.15' d='M100 20c-8 10-4 22 6 26 8 4 20-2 22-12 2-9-4-18-14-18-4 0-9 1-14 4z'/%3E%3C/svg%3E\")", backgroundSize: '160px' }} />
        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-14 md:py-20 relative">
          <div className="text-white max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-brand text-white flex items-center justify-center shadow-xl shadow-brand/40">
                <Camera className="w-8 h-8" />
              </div>
              <div className="text-xs md:text-sm uppercase tracking-widest text-white/80 font-semibold">
                photos & videos<br />mbbs in russia
              </div>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1] tracking-tight">
              GALLERY
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/90 max-w-3xl leading-relaxed">
              Peek inside our partner universities, campus life, city tours, alumni journeys, guidance
              seminars and India–Russia community moments — {photoCount} photos and {videoCount} videos.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/universities" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-brand hover:bg-brand-dark text-white font-bold btn-shift transition-colors" data-testid="gallery-universities-btn">
                See Universities <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/faqs" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white/10 border border-white/30 text-white font-bold hover:bg-white/20 transition-colors">
                FAQs
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <section className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-border">
        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-4">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex flex-wrap items-center gap-2" data-testid="kind-tabs">
              {KINDS.map((k) => {
                const Icon = k.icon;
                const isActive = kind === k.id;
                return (
                  <button
                    key={k.id}
                    onClick={() => setKind(k.id)}
                    data-testid={`kind-${k.id}`}
                    className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold border transition-all
                      ${isActive
                        ? 'bg-brand text-white border-brand shadow-md shadow-brand/30'
                        : 'bg-white text-primary border-border hover:border-brand/50 hover:text-brand'}`}
                  >
                    <Icon className="w-4 h-4" />
                    {k.label}
                    {k.id !== 'all' && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-muted'}`}>
                        {k.id === 'photo' ? photoCount : videoCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2" data-testid="category-tabs">
              <button
                onClick={() => setCategory('all')}
                data-testid="category-all"
                className={`h-9 px-3 rounded-full text-xs font-semibold border transition-all
                  ${category === 'all' ? 'bg-primary text-white border-primary' : 'bg-white text-primary border-border hover:border-primary/50'}`}
              >
                All Categories
              </button>
              {galleryCategories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  data-testid={`category-${c.id}`}
                  className={`h-9 px-3 rounded-full text-xs font-semibold border transition-all
                    ${category === c.id ? 'bg-primary text-white border-primary' : 'bg-white text-primary border-border hover:border-primary/50'}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-10 md:py-14">
        <div className="container mx-auto max-w-7xl px-4 md:px-8">
          <div className="mb-5 text-sm text-muted-foreground" data-testid="gallery-count">
            Showing <span className="text-primary font-bold">{filtered.length}</span> item
            {filtered.length === 1 ? '' : 's'}
          </div>

          {filtered.length === 0 ? (
            <div className="p-16 text-center bg-muted/40 rounded-2xl text-sm text-muted-foreground">
              No items match the selected filters.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[220px] md:auto-rows-[240px] gap-3 md:gap-4" data-testid="gallery-grid">
              {filtered.map((it, idx) => (
                <button
                  key={it.id}
                  onClick={() => setActiveIdx(idx)}
                  data-testid={`gallery-item-${it.id}`}
                  className={`group relative overflow-hidden rounded-xl bg-muted border border-border hover:border-brand/50 transition-all shadow-sm hover:shadow-xl ${spanClass(it)}`}
                >
                  <img
                    src={it.kind === 'video' ? it.poster : it.src}
                    alt={it.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                  {/* Type badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest
                      ${it.kind === 'video' ? 'bg-brand text-white' : 'bg-white/90 text-primary'}`}>
                      {it.kind === 'video' ? <VideoIcon className="w-3 h-3" /> : <Camera className="w-3 h-3" />}
                      {it.kind}
                    </span>
                  </div>

                  {/* Play button for videos */}
                  {it.kind === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-brand text-white flex items-center justify-center shadow-2xl shadow-black/40 group-hover:scale-110 transition-transform">
                        <Play className="w-6 h-6 ml-0.5" fill="currentColor" />
                      </div>
                    </div>
                  )}

                  {/* Caption */}
                  <div className="absolute bottom-0 inset-x-0 p-3 text-left text-white translate-y-4 group-hover:translate-y-0 transition-transform">
                    <div className="text-[10px] uppercase tracking-widest text-brand font-bold">
                      {galleryCategories.find((c) => c.id === it.category)?.label || it.category}
                    </div>
                    <div className="text-sm font-serif font-bold leading-tight line-clamp-2">{it.title}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox modal */}
      {active && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-brand text-white flex items-center justify-center transition-colors"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-brand text-white flex items-center justify-center transition-colors"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next"
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-brand text-white flex items-center justify-center transition-colors"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="relative rounded-2xl overflow-hidden bg-black">
              {active.kind === 'video' ? (
                <div className="aspect-video">
                  <iframe
                    key={active.id}
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${active.youtubeId}?autoplay=1&rel=0`}
                    title={active.title}
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="lightbox-video"
                  />
                </div>
              ) : (
                <img
                  src={active.src}
                  alt={active.title}
                  className="w-full max-h-[80vh] object-contain bg-black"
                  data-testid="lightbox-image"
                />
              )}
            </div>
            <div className="mt-4 flex items-center justify-between text-white">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-brand font-bold">
                  {galleryCategories.find((c) => c.id === active.category)?.label || active.category}
                </div>
                <div className="font-serif text-lg md:text-xl font-bold mt-1">{active.title}</div>
              </div>
              <div className="text-xs text-white/70">
                {activeIdx + 1} / {filtered.length}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="py-14 md:py-16 bg-muted/40">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-primary text-white p-8 md:p-10">
            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand font-bold mb-2">See it live</div>
                <h3 className="font-serif text-2xl md:text-3xl font-bold leading-tight">
                  Want a personal virtual tour of a specific Russian medical university?
                </h3>
              </div>
              <a href="tel:+919509406802" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-bold h-12 px-7 rounded-full btn-shift shrink-0" data-testid="gallery-call-cta">
                Call +91 95094 06802
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
